import * as Sentry from '@sentry/browser'
import { AppError, getAppStatus } from 'single-spa'
import type { BrowserClientOptions } from '@sentry/browser/types/client'

function isAppError(e: unknown): e is AppError {
  return (
    (e as AppError).appOrParcelName !== null && (e as AppError).message !== null
  )
}

function sentryHubClient(options: BrowserClientOptions): {
  hub: Sentry.Hub
  errorHandler: (error: unknown) => void
  startSessionTrackingOnHub: () => void
} {
  options.beforeSend = (event) => {
    const request = {
      url: window.location.href,
      headers: { 'User-Agent': navigator.userAgent },
    }

    if (event.request) {
      Object.assign(event.request, request)
    } else {
      event.request = request
    }

    if (options.environment !== 'production') {
      console.info('Sending event to app origin', event)
    }

    return event
  }

  options.release = `customer-platform@${options.release || 'latest'}`

  // init hub
  const client = new Sentry.BrowserClient(options)
  const hub = new Sentry.Hub(client)

  const errorHandler = (err): void => {
    hub.withScope((scope) => {
      scope.setExtra('host', 'customer-root-config')
      if (isAppError(err)) {
        scope.setExtra('appName', err.appOrParcelName)
        scope.setExtra('message', err.message)
        scope.setExtra('app_status', getAppStatus(err.appOrParcelName))
        scope.setTag('mf_error_code', getAppStatus(err.appOrParcelName))
        scope.setTag('mf_name', err.appOrParcelName)
      }
      hub.captureException(err)
    })
  }

  const startSessionTrackingOnHub = (): void => {
    hub.startSession({ ignoreDuration: true })
    hub.captureSession()
  }

  return {
    hub,
    errorHandler,
    startSessionTrackingOnHub,
  }
}

export default sentryHubClient
